import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import HeaderWithChildrenWrapper from 'shared/components/molecules/HeaderWithChildrenWrapper';
import TextInput from 'shared/components/atoms/TextInput';
import TextAreaInput from 'shared/components/atoms/TextAreaInput';
import Dropdown, { IDropdownOption } from 'shared/components/molecules/Dropdown';
import Button, { ButtonMainStyle } from 'shared/components/atoms/Button';
import LinkButton from 'shared/components/atoms/LinkButton';
import Alert, { Alerts } from 'shared/components/atoms/Alert';
import Column from 'shared/components/atoms/Column';
import CardWithInitialText, { CardState } from 'shared/components/organisms/CardWithInitialText';
import { AlignContentRight, FlexSpacedMd } from 'shared/cssUtils';
import { useDispatch, useSelector } from 'react-redux';
import { selectCompanyData } from 'User/selectors';
import { showFullName } from 'shared/utils/nameHelper';
import { IAppState } from 'reducers';
import { buildCreateCeaseFormDefaults, createCease, resetCeaseError, setActiveCustomer } from '../actions';
import { RequestLifecycle } from 'shared/actions';
import { selectCeaseCreateState, selectCeaseErrorMessage, selectCeaseLastCreated } from '../selectors';
import { CeaseFormValues, CeaseReason, CeaseRequestDetails, CeaseRequestType, ceaseRequestContentByType } from '../types/cease';
import { ceaseById, ceaseList } from 'CustomerRequests/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { pages, useTrackPage } from 'shared/utils/trackPage';
import { getCustomerFromCompanyData } from 'CustomerRequests/utils/getCustomerFromCompanyData';
import { getRequestTypeFromCategory } from '../utils/requestType';

type SubmitCeaseRouteState = {
  copyFromCease?: CeaseRequestDetails;
  customerId?: string;
  customerName?: string;
} | null;

const SubmitCeaseRequest: FC = () => {
  useTrackPage(pages.submitCeaseRequest);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as SubmitCeaseRouteState;

  const companyData = useSelector(selectCompanyData);
  const { customerId: globalCustomerId, customerName: globalCustomerName } = getCustomerFromCompanyData(companyData);
  const customerId = navigationState?.customerId || globalCustomerId;
  const customerName = navigationState?.customerName || globalCustomerName;

  const userData = useSelector((state: IAppState) => state.user.userData);
  const submittedByName = showFullName(userData.firstName, userData.lastName);

  const createState = useSelector(selectCeaseCreateState);
  const errorMessage = useSelector(selectCeaseErrorMessage);
  const lastCreated = useSelector(selectCeaseLastCreated);

  const previousCreateState = useRef<RequestLifecycle | null>(null);
  const hasAppliedCopyState = useRef(false);

  const [values, setValues] = useState<CeaseFormValues>(() =>
    buildCreateCeaseFormDefaults(customerName, submittedByName, submittedByName, userData.contactEmail)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const requestTypeContent =
    values.requestType === CeaseRequestType.CeaseQuote
      ? ceaseRequestContentByType[CeaseRequestType.CeaseQuote]
      : ceaseRequestContentByType[CeaseRequestType.CeaseRequest];

  useEffect(() => {
    if (!customerId) return;
    dispatch(setActiveCustomer(customerId));
  }, [customerId, dispatch]);

  useEffect(() => {
    dispatch(resetCeaseError());
  }, [dispatch]);

  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      customerName,
      submittedBy: submittedByName,
      primaryContactName: prev.primaryContactName,
      primaryContactEmail: prev.primaryContactEmail,
    }));
  }, [customerName, submittedByName]);

  useEffect(() => {
    if (hasAppliedCopyState.current) return;
    if (!navigationState?.copyFromCease) return;

    const copied = navigationState.copyFromCease;
    const requestedCeaseDate = copied.requestedCeaseDate ? dayjs(copied.requestedCeaseDate).toDate() : null;
    const ceaseReason = Object.values(CeaseReason).includes(copied.ceaseReason as CeaseReason)
      ? (copied.ceaseReason as CeaseReason)
      : '';

    setValues((prev) => ({
      ...prev,
      requestType: getRequestTypeFromCategory(copied.category, copied.subCategory),
      primaryContactName: copied.primaryContactName || prev.primaryContactName,
      primaryContactEmail: copied.primaryContactEmail || prev.primaryContactEmail,
      primaryContactPhone: copied.primaryContactPhone || '',
      secondaryContactEmail: copied.secondaryContactEmail || '',
      customerReference: copied.customerReference || '',
      ceaseReason,
      requestedCeaseDate,
      details: copied.details || '',
      idn: '', // IDN is intentionally left blank when copying, as it should be unique for each request
    }));

    hasAppliedCopyState.current = true;
  }, [navigationState]);

  useEffect(() => {
    const prev = previousCreateState.current;
    previousCreateState.current = createState;

    // Only redirect after a *new* successful submit.
    // Otherwise revisiting `/requests/cease/new` would immediately redirect again
    // because the Redux `createState` remains `Success`.
    if (prev === RequestLifecycle.Loading && createState === RequestLifecycle.Success && lastCreated?.id) {
      navigate(ceaseById(lastCreated.id), { state: { ceaseCreated: true } });
    }
  }, [createState, lastCreated?.id, navigate]);

  const ceaseReasonOptions: IDropdownOption[] = useMemo(() => {
    return Object.values(CeaseReason).map((v) => ({
      label: v,
      value: v,
    }));
  }, []);

  const requestTypeOptions: IDropdownOption[] = useMemo(
    () => [
      { label: CeaseRequestType.CeaseRequest, value: CeaseRequestType.CeaseRequest },
      { label: CeaseRequestType.CeaseQuote, value: CeaseRequestType.CeaseQuote },
    ],
    []
  );

  const validate = (form: CeaseFormValues): Record<string, string> => {
    const next: Record<string, string> = {};

    if (!form.requestType) next.requestType = 'Request type is required';
    if (!form.primaryContactName.trim()) next.primaryContactName = 'Primary contact name is required';
    if (!form.primaryContactEmail.trim()) next.primaryContactEmail = 'Primary contact email is required';
    if (!form.primaryContactPhone.trim()) next.primaryContactPhone = 'Primary contact phone is required';
    if (!form.idn.trim()) next.idn = 'IDN is required';
    if (!form.ceaseReason) next.ceaseReason = 'Cease reason is required';

    if (!form.requestedCeaseDate) {
      next.requestedCeaseDate = 'Requested cease date is required';
    } else {
      const today = dayjs().startOf('day');
      const selected = dayjs(form.requestedCeaseDate).startOf('day');
      if (selected.isBefore(today)) next.requestedCeaseDate = 'Requested cease date cannot be in the past';
    }

    if (form.secondaryContactEmail && form.secondaryContactEmail.trim().length > 0) {
      const email = form.secondaryContactEmail.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        next.secondaryContactEmail = 'Secondary contact email must be a valid email address';
      }
    }

    return next;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) return;

    setSubmitAttempted(true);

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    dispatch(resetCeaseError());
    dispatch(
      createCease({
        form: values,
        customerId,
        customerName,
        submittedBy: userData.id,
      })
    );
  };

  const isSubmitting = createState === RequestLifecycle.Loading;

  const setField = <K extends keyof CeaseFormValues>(key: K, value: CeaseFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key as string]) return prev;
      const copy = { ...prev };
      delete copy[key as string];
      return copy;
    });
  };

  return (
    <HeaderWithChildrenWrapper header="Submit cease request">
      <Container>
        {errorMessage && <Alert alertType={Alerts.DANGER}>{errorMessage}</Alert>}

        <form onSubmit={onSubmit}>
          <CardWithInitialText title='Request type' cardState={CardState.Edit} onEditClick={() => undefined} className="mb-4">
            <div className="row no-gutters">
              <Column smWidth={12} mdWidth={5} className="section-description">
                <p className="mb-0 text-muted">Select the type of cease request you want to submit.</p>
              </Column>
              <Column smWidth={12} mdWidth={7}>
                <div className="form-group mb-1">
                  <Dropdown
                    options={requestTypeOptions}
                    placeholder="Select..."
                    isClearable={false}
                    value={
                      values.requestType
                        ? ({ label: values.requestType, value: values.requestType } as IDropdownOption)
                        : null
                    }
                    onChange={(selection) => setField('requestType', (selection?.value as CeaseRequestType) || '')}
                  />
                  {errors.requestType && <div className="text-danger small">{errors.requestType}</div>}
                </div>
              </Column>
            </div>

            <div className="row mt-3">
              <Column defaultWidth={12}>
                <div className="request-type-content">
                  <h4 className="mb-3">{requestTypeContent.title}</h4>
                  <Alert alertType={Alerts.INFO} className="mb-0">
                    Notes:
                    <ul className="mb-0">
                      {requestTypeContent.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </Alert>
                </div>
              </Column>
            </div>
          </CardWithInitialText>

          <div className="cards">
            <CardWithInitialText title="Customer details" cardState={CardState.Edit} onEditClick={() => undefined} className="mb-4">
              <div className="row no-gutters">
                <Column smWidth={12} mdWidth={5} className="section-description">
                  <p className="mb-0 text-muted">
                    Confirm your customer details are correct.
                  </p>
                </Column>
                <Column smWidth={12} mdWidth={7}>
                  <div className="row">
                    <Column smWidth={12} mdWidth={6}>
                      <TextInput
                        fieldName="customerName"
                        inputIdentifierPrefix="cease-"
                        labelText="Customer name"
                        value={values.customerName}
                        readOnly
                        onChange={() => undefined as any}
                      />
                    </Column>
                    <Column smWidth={12} mdWidth={6}>
                      <TextInput
                        fieldName="submittedBy"
                        inputIdentifierPrefix="cease-"
                        labelText="Submitted by"
                        value={values.submittedBy}
                        readOnly
                        onChange={() => undefined as any}
                      />
                    </Column>
                  </div>
                </Column>
              </div>
            </CardWithInitialText>

            <CardWithInitialText title="Primary contact" cardState={CardState.Edit} onEditClick={() => undefined} className="mb-4">
              <div className="row no-gutters">
                <Column smWidth={12} mdWidth={5} className="section-description">
                  <p className="mb-0 text-muted">
                    This contact is used for questions and updates for this cease request.
                  </p>
                </Column>
                <Column smWidth={12} mdWidth={7}>
                  <div className="row">
                    <Column smWidth={12} mdWidth={6}>
                      <TextInput
                        fieldName="primaryContactName"
                        inputIdentifierPrefix="cease-"
                        labelText="Full name"
                        value={values.primaryContactName}
                        onChange={(e) => setField('primaryContactName', e.target.value)}
                      />
                      {errors.primaryContactName && <div className="text-danger small">{errors.primaryContactName}</div>}
                    </Column>
                    <Column smWidth={12} mdWidth={6}>
                      <TextInput
                        fieldName="primaryContactEmail"
                        inputIdentifierPrefix="cease-"
                        labelText="Email"
                        type="email"
                        value={values.primaryContactEmail}
                        onChange={(e) => setField('primaryContactEmail', e.target.value)}
                      />
                      {errors.primaryContactEmail && <div className="text-danger small">{errors.primaryContactEmail}</div>}
                    </Column>
                  </div>

                  <div className="row">
                    <Column smWidth={12} mdWidth={6}>
                      <TextInput
                        fieldName="primaryContactPhone"
                        inputIdentifierPrefix="cease-"
                        labelText="Phone"
                        value={values.primaryContactPhone}
                        onChange={(e) => setField('primaryContactPhone', e.target.value)}
                      />
                      {errors.primaryContactPhone && <div className="text-danger small">{errors.primaryContactPhone}</div>}
                    </Column>
                  </div>
                </Column>
              </div>
            </CardWithInitialText>

            <CardWithInitialText title="Secondary contact" cardState={CardState.Edit} onEditClick={() => undefined} className="mb-4">
              <div className="row no-gutters">
                <Column smWidth={12} mdWidth={5} className="section-description">
                  <p className="mb-0 text-muted">
                    Add an additional email address to be informed of this cease request.
                  </p>
                </Column>
                <Column smWidth={12} mdWidth={7}>
                  <div className="row">
                    <Column smWidth={12} mdWidth={6}>
                      <TextInput
                        fieldName="secondaryContactEmail"
                        inputIdentifierPrefix="cease-"
                        labelText="Email"
                        optional
                        type="email"
                        helperText=""
                        value={values.secondaryContactEmail}
                        onChange={(e) => setField('secondaryContactEmail', e.target.value)}
                      />
                      {errors.secondaryContactEmail && (
                        <div className="text-danger small">{errors.secondaryContactEmail}</div>
                      )}
                    </Column>
                  </div>
                </Column>
              </div>
            </CardWithInitialText>

            <CardWithInitialText title="Service details" cardState={CardState.Edit} onEditClick={() => undefined} className="mb-4">
              <div className="row no-gutters">
                <Column smWidth={12} mdWidth={5} className="section-description">
                  <p className="mb-0 text-muted">
                    Provide the IDN/service identifier and requested cease date.
                    <br /><br />
                    Note that the cease date is subject to contractual notice periods.
                  </p>
                </Column>
                <Column smWidth={12} mdWidth={7}>
                  <div className="row">
                    <Column smWidth={12} mdWidth={8}>
                      <TextInput
                        fieldName="customerReference"
                        inputIdentifierPrefix="cease-"
                        labelText="Customer reference"
                        optional
                        value={values.customerReference}
                        onChange={(e) => setField('customerReference', e.target.value)}
                      />
                    </Column>
                    <Column smWidth={12} mdWidth={4}>
                      <TextInput
                        fieldName="idn"
                        inputIdentifierPrefix="cease-"
                        labelText="IDN"
                        value={values.idn}
                        onChange={(e) => setField('idn', e.target.value)}
                      />
                      {errors.idn && <div className="text-danger small">{errors.idn}</div>}
                    </Column>

                  </div>

                  <div className="row">
                    <Column smWidth={12} mdWidth={8}>
                      <div className="form-group mb-1">
                        <label className="small mb-1">Cease reason</label>
                        <Dropdown
                          options={ceaseReasonOptions}
                          placeholder="Select..."
                          value={
                            values.ceaseReason
                              ? ({ label: values.ceaseReason, value: values.ceaseReason } as any)
                              : null
                          }
                          onChange={(selection) => setField('ceaseReason', selection?.value)}
                        />
                        {errors.ceaseReason && <div className="text-danger small">{errors.ceaseReason}</div>}
                      </div>
                    </Column>
                    <Column smWidth={12} mdWidth={4}>
                      <div className="form-group mb-1">
                        <label htmlFor="cease-ceaseDate" className="small mb-1">
                          Requested cease date
                        </label>
                        <input
                          id="cease-ceaseDate"
                          className="form-control"
                          type="date"
                          value={values.requestedCeaseDate ? dayjs(values.requestedCeaseDate).format('YYYY-MM-DD') : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField('requestedCeaseDate', v ? dayjs(v, 'YYYY-MM-DD').toDate() : null);
                          }}
                        />
                        {errors.requestedCeaseDate && (
                          <div className="text-danger small">{errors.requestedCeaseDate}</div>
                        )}
                      </div>
                    </Column>
                  </div>

                  <div className="row">
                    <Column defaultWidth={12}>
                      <TextAreaInput
                        fieldName="details"
                        inputIdentifierPrefix="cease-"
                        labelText="Additional details"
                        optional
                        maxLength={1000}
                        value={values.details}
                        onChange={(e) => setField('details', e.target.value)}
                        placeholder="Please provide any additional information that may help us process your cease request."
                      />
                    </Column>
                  </div>
                </Column>
              </div>
            </CardWithInitialText>
          </div>

          {submitAttempted && Object.keys(errors).length > 0 && (
            <Alert alertType={Alerts.DANGER} className="mb-3">
              Please complete all required fields before submitting.
            </Alert>
          )}

          <div className="row">
            <Column defaultWidth={6}>
              <LinkButton pathname={ceaseList} buttonText="Back to cease requests" mainStyle={ButtonMainStyle.LinkPrimary} />
            </Column>
            <Column defaultWidth={6}>
                <AlignContentRight>
                    <FlexSpacedMd>
                      <LinkButton pathname={ceaseList} buttonText="Cancel" mainStyle={ButtonMainStyle.PrimaryRectangular} />
                      <Button
                          type="submit"
                          mainStyle={ButtonMainStyle.SecondaryRectangular}
                          disabled={!customerId || isSubmitting}
                          saving={isSubmitting}
                          className="mr-0"
                      >
                          Submit
                      </Button>
                    </FlexSpacedMd>
                </AlignContentRight>
            </Column>
          </div>
        </form>
      </Container>
    </HeaderWithChildrenWrapper>
  );
};

const Container = styled.div`
  .cards {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
  }

  /* Hide the step/status icon shown in CardWithInitialText headers on this form */
  section .header h4 > i {
    display: none;
  }

  .section-description {
    padding-right: 1.5rem;
    margin-bottom: 1rem;
  }
`;

export default SubmitCeaseRequest;
